import { theme } from '@store';
import { amber } from '@mui/material/colors';
import { FC } from 'react';
import { WatchLater, ChevronRight } from '@mui/icons-material';
import { Box, Chip, Tooltip, Typography } from '@mui/material';

enum UrgencyCCMLevel {
  ACCEPTABLE = 'ACCEPTABLE',
  WARNING = 'warning',
  CRITICAL = 'critical',
  DEFAULT = 'default',
}

const getUrgencyCCMLevel = (minutes: number | null): UrgencyCCMLevel => {
  if (!minutes) return UrgencyCCMLevel.DEFAULT;

  if (minutes > 119) return UrgencyCCMLevel.ACCEPTABLE;
  if (minutes > 109) return UrgencyCCMLevel.CRITICAL;
  if (minutes > 59) return UrgencyCCMLevel.WARNING;
  if (minutes > 49) return UrgencyCCMLevel.CRITICAL;
  if (minutes > 19) return UrgencyCCMLevel.WARNING;
  if (minutes > 14) return UrgencyCCMLevel.CRITICAL;

  return UrgencyCCMLevel.DEFAULT;
};

const urgencyCCMChipStyles = {
  [UrgencyCCMLevel.ACCEPTABLE]: {
    border: theme.palette.success.main,
    bg: theme.palette.green.main,
    text: theme.palette.success.contrastText,
    icon: theme.palette.success.dark,
  },
  [UrgencyCCMLevel.WARNING]: {
    border: theme.palette.darkRed.main,
    bg: amber[100],
    text: theme.palette.darkRed.main,
    icon: theme.palette.warningDark.main,
  },
  [UrgencyCCMLevel.CRITICAL]: {
    border: theme.palette.darkRed.main,
    bg: theme.palette.salmon.main,
    text: theme.palette.darkRed.main,
    icon: theme.palette.errorDark.main,
  },
  [UrgencyCCMLevel.DEFAULT]: {
    border: theme.palette.grey[300],
    bg: theme.palette.grey[300],
    text: theme.palette.semiBlack.main,
    icon: theme.palette.semiBlack.main,
  },
} as const;

interface MinutesChipProps {
  minutes: number;
  label?: string;
  hasIcon?: boolean;
  clickable?: boolean;
}

export const MinutesChip: FC<MinutesChipProps> = ({
  minutes,
  label,
  hasIcon,
  clickable,
}) => {
  const urgencyCCMLevel = getUrgencyCCMLevel(minutes);

  return (
    <Tooltip
      title='View CM activities'
      disableFocusListener={!clickable}
      disableHoverListener={!clickable}
      disableTouchListener={!clickable}
    >
      <Chip
        size='small'
        variant='outlined'
        label={
          <Box
            // component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: clickable ? 'pointer' : 'unset',
            }}
          >
            {hasIcon && (
              <WatchLater
                sx={{
                  color: urgencyCCMChipStyles[urgencyCCMLevel].icon,
                  width: 16,
                  height: 16,
                }}
              />
            )}
            {label && (
              <Typography
                variant='caption'
                sx={{ fontSize: 14, fontWeight: 400 }}
              >
                {label}
              </Typography>
            )}
            <Typography
              variant='caption'
              sx={{ fontSize: 14, fontWeight: 500, padding: 0 }}
            >
              {minutes}
            </Typography>
            {clickable && <ChevronRight sx={{ width: 16, height: 16 }} />}
          </Box>
        }
        sx={{
          borderColor: urgencyCCMChipStyles[urgencyCCMLevel].border,
          bgcolor: urgencyCCMChipStyles[urgencyCCMLevel].bg,
          color: urgencyCCMChipStyles[urgencyCCMLevel].text,

          '& .MuiChip-icon': {
            color: urgencyCCMChipStyles[urgencyCCMLevel].icon,
            width: 16,
            height: 16,
          },

          '& .MuiChip-label': {
            py: 0,
          },
        }}
      />
    </Tooltip>
  );
};
